import type { MouseEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { useFormikContext, useTheme, useThemeColor, Button, Text } from '@wistia/vhs';
import { MentionsInput, Mention } from 'react-mentions';
import type { DefaultTheme } from 'styled-components';
import { trackStructEvent } from '~/utilities/snowplow';
import { isNotNil } from '~/utilities/type-guards';
import { useCommentContext } from '../CommentContext';

const COMMON_HEIGHT = 80;
const MENTIONS_TO_SHOW = 5;

// Note: It's important for these to stay the same
// across the different elements in MentionsInput
const commonStyles = () => ({
  overflowY: 'auto',
  minHeight: `${COMMON_HEIGHT}px`,
  fontFamily: 'Inter, Helvetica, Arial, sans-serif',
});

// Likewise, it's important for there to be consistency
// between the highlighter and input elements.
const highlighterOrInputStyles = (color: string, focusColor: string, isFocused: boolean) => ({
  padding: 9,
  borderColor: isFocused ? focusColor : color,
  outline: isFocused && 'none',
  lineHeight: '20px',
  boxSizing: 'border-box',
  borderRadius: '4px',
});

const mentionStyles = (color: string) => ({
  backgroundColor: color,
  borderRadius: '1px',
});

const mentionInputStyles = (isFocused: boolean, theme: DefaultTheme) => ({
  fontSize: '14px',
  suggestions: {
    list: {
      maxHeight: COMMON_HEIGHT * MENTIONS_TO_SHOW,
      boxShadow: theme.shadow.elevation2,
      width: '100%',
      zIndex: theme.zIndex.ContactSelect,
      backgroundColor: 'white',
      borderRadius: '6px',
      cursor: 'pointer',
      overflowY: 'scroll',
      padding: '10px',
      transition: 'all 0.15s ease-in-out',
    },
  },
  '&multiLine': {
    control: {
      ...commonStyles(),
    },
    highlighter: {
      backgroundColor: 'white',
      ...highlighterOrInputStyles('transparent', theme.color.brandBlue500, isFocused),
      ...commonStyles(),
    },
    input: {
      ...highlighterOrInputStyles('#c4c4c8', theme.color.brandBlue500, isFocused),
      ...commonStyles(),
    },
  },
});

const HighlightedText = ({
  index,
  search,
  text,
}: {
  index: number;
  search: string;
  text: string;
}): JSX.Element | string =>
  index === -1 ? (
    text
  ) : (
    <>
      {text.substring(0, index)}
      <b>{text.substring(index, index + search.length)}</b>
      {text.substring(index + search.length)}
    </>
  );

export const BodyFieldWithContactSelect = ({
  fieldRef,
  placeholder,
  onFocus,
  autoFocus,
  trackingLabel = 'media-page',
}: {
  fieldRef: React.RefObject<HTMLInputElement>;
  placeholder?: string;
  onFocus?: () => void;
  autoFocus?: boolean;
  trackingLabel?: string;
}): JSX.Element => {
  const theme = useTheme();
  const [brandBlue100, grey700] = useThemeColor(['brandBlue100', 'grey700']);

  const { values, setFieldValue } = useFormikContext() as {
    values: { body: string };
    setFieldValue: (field: string, value: string) => void;
  };

  const [bodyValue, setBodyValue] = useState(values.body);

  const { formattedContactsForTagging } = useCommentContext();

  useEffect(() => {
    if (values.body === bodyValue) {
      return;
    }
    setBodyValue(values.body);
  }, [values.body, bodyValue]);

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setBodyValue(evt.target.value);
    setFieldValue('body', evt.target.value);
  };

  return (
    <div
      style={{
        position: 'relative',
        flexGrow: '1',
        width: '100%',
      }}
    >
      <MentionsInput
        data-testid="mentions-input"
        value={bodyValue}
        inputRef={fieldRef}
        onFocus={onFocus}
        autoFocus={autoFocus}
        onChange={handleChange}
        allowSuggestionsAboveCursor={true}
        placeholder={placeholder}
        style={mentionInputStyles(autoFocus ?? false, theme)}
        onClick={(event: MouseEvent) => {
          event.preventDefault();
        }}
      >
        <Mention
          trigger="@"
          appendSpaceOnAdd={true}
          data={formattedContactsForTagging}
          displayTransform={(_id: string, display: string) =>
            `@${display.split(' ').slice(0, -1).join(' ') || display}`
          }
          renderSuggestion={(
            props: { id: string; name?: string; email: string },
            search: string,
          ) => {
            const nameOrEmail = props.name ?? props.email;
            const nameIndex = props.name?.toLowerCase().indexOf(search.toLowerCase()) ?? -1;
            const emailIndex = props.email.toLowerCase().indexOf(search.toLowerCase());
            return (
              <Button
                key={props.id}
                colorOverride="white"
                style={{ width: '100%', display: 'block' }}
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  trackStructEvent({
                    category: 'button',
                    action: 'click',
                    label: `${trackingLabel}-team-comment-tag-user`,
                  });
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text variant="body2">
                    <HighlightedText
                      index={nameIndex}
                      search={search}
                      text={nameOrEmail}
                    />
                  </Text>
                  {isNotNil(props.name) ? (
                    <Text
                      variant="caption"
                      colorOverride={grey700}
                    >
                      <HighlightedText
                        index={emailIndex}
                        search={search}
                        text={props.email}
                      />
                    </Text>
                  ) : null}
                </div>
              </Button>
            );
          }}
          style={mentionStyles(brandBlue100)}
        />
      </MentionsInput>
    </div>
  );
};
